import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  styled,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Lock,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

interface ResetPasswordResponse {
  message: string;
}

const BackgroundContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  background: theme.palette.mode === 'dark'
    ? '#313131'
    : `
      radial-gradient(circle at 20% 80%, rgba(102, 126, 234, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(118, 75, 162, 0.1) 0%, transparent 50%),
      linear-gradient(135deg, #FAFBFC 0%, #F4F6F8 100%)
    `,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2),
  position: 'relative',
  overflow: 'hidden',
}));

const ResetPasswordCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(5),
  width: '100%',
  maxWidth: 480,
  background: theme.palette.mode === 'dark' ? '#1a1a1a' : 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(20px)',
  borderRadius: 24,
  border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.06)' : '1px solid rgba(255, 255, 255, 0.3)',
  boxShadow: theme.palette.mode === 'dark' ? '0 20px 60px rgba(0, 0, 0, 0.4)' : '0 20px 60px rgba(0, 0, 0, 0.08)',
  position: 'relative',
  overflow: 'hidden',

  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    background: 'linear-gradient(90deg, #1a1a1a 0%, #1a1a1a 100%)',
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    backgroundColor: theme.palette.mode === 'dark' ? '#313131' : alpha(theme.palette.primary.main, 0.02),
    transition: 'all 0.3s ease',

    '& fieldset': {
      borderColor: theme.palette.mode === 'dark' ? '#313131' : alpha(theme.palette.primary.main, 0.1),
      borderWidth: 2,
    },

    '&:hover fieldset': {
      borderColor: theme.palette.mode === 'dark' ? '#313131' : alpha(theme.palette.primary.main, 0.3),
    },

    '&.Mui-focused': {
      backgroundColor: theme.palette.mode === 'dark' ? '#313131' : '#FFFFFF',

      '& fieldset': {
        borderColor: theme.palette.primary.main,
        borderWidth: 2,
      },
    },
  },

  '& .MuiInputLabel-root': {
    color: theme.palette.text.secondary,

    '&.Mui-focused': {
      color: theme.palette.primary.main,
    },
  },

  '& .MuiInputAdornment-root': {
    color: theme.palette.mode === 'dark' ? alpha('#E5E7EB', 0.7) : alpha(theme.palette.primary.main, 0.6),
  },
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  padding: '14px 32px',
  fontSize: '1rem',
  fontWeight: 600,
  textTransform: 'none',
  background: '#1a1a1a',
  boxShadow: theme.palette.mode === 'dark' ? '0 8px 24px rgba(0, 0, 0, 0.6)' : '0 8px 24px rgba(44, 62, 80, 0.3)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',

  '&:hover': {
    background: '#34495e',
    transform: 'translateY(-2px)',
    boxShadow: theme.palette.mode === 'dark' ? '0 12px 32px rgba(0, 0, 0, 0.7)' : '0 12px 32px rgba(44, 62, 80, 0.4)',
  },

  '&:disabled': {
    background: theme.palette.action.disabledBackground,
    boxShadow: 'none',
  },
}));

const ResetPasswordPage: React.FC = () => {
  const theme = useTheme();
  const { markPasswordResetCompleted } = useAuth();
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (tokenParam) {
      setToken(tokenParam);
    } else {
      navigate('/');
    }
  }, [searchParams, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');

    if (newPassword !== confirmPassword) {
      setMessage('As senhas não coincidem');
      return;
    }

    if (newPassword.length < 6) {
      setMessage('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    setLoading(true);

    try {
      const response = await api.post<ResetPasswordResponse>('/Authentication/ResetPassword', {
        token,
        newPassword,
        confirmPassword,
      });

      if (response.message) {
        setMessage(response.message);
        setIsSuccess(true);

        if (markPasswordResetCompleted) {
          markPasswordResetCompleted();
        }
        
        // Redirecionar para login após 3 segundos
        setTimeout(() => {
          navigate('/');
        }, 3000);
      }
    } catch (error: any) {
      setMessage(error.response?.data?.message || 'Erro ao redefinir senha');
      setIsSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <BackgroundContainer>
      <Container component="main" maxWidth="sm">
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <ResetPasswordCard elevation={0}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  background: theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, #65f0c5 0%, #65f0c5 100%)'
                    : 'linear-gradient(135deg, #1a1a1a 0%, rgb(75, 106, 137) 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  mb: 1,
                }}
              >
                Redefinir Senha
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Digite sua nova senha abaixo
              </Typography>
            </Box>

            {message && (
              <Alert
                severity={isSuccess ? 'success' : 'error'}
                sx={{
                  mb: 3,
                  borderRadius: 2,
                }}
              >
                {message}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <StyledTextField
                margin="normal"
                required
                fullWidth
                name="newPassword"
                label="Nova Senha"
                type={showPassword ? 'text' : 'password'}
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={loading || isSuccess}
                InputProps={{
                  startAdornment: (
                    <Lock sx={{ mr: 1, color: 'action.active' }} />
                  ),
                  endAdornment: (
                    <Box
                      component="span"
                      onClick={() => setShowPassword(!showPassword)}
                      sx={{
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        color: 'action.active',
                      }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </Box>
                  ),
                }}
              />

              <StyledTextField
                margin="normal"
                required
                fullWidth
                name="confirmPassword"
                label="Confirmar Nova Senha"
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading || isSuccess}
                InputProps={{
                  startAdornment: (
                    <Lock sx={{ mr: 1, color: 'action.active' }} />
                  ),
                  endAdornment: (
                    <Box
                      component="span"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      sx={{
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        color: 'action.active',
                      }}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </Box>
                  ),
                }}
              />

              <SubmitButton
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading || isSuccess}
                sx={{ mt: 4, mb: 3 }}
              >
                {loading ? (
                  <CircularProgress size={24} sx={{ color: 'white' }} />
                ) : (
                  'Redefinir Senha'
                )}
              </SubmitButton>

              <Box sx={{ textAlign: 'center' }}>
                <Button
                  onClick={() => navigate('/')}
                  disabled={loading || isSuccess}
                  variant="text"
                >
                  Voltar ao Login
                </Button>
              </Box>
            </Box>
          </ResetPasswordCard>
        </Box>
      </Container>
    </BackgroundContainer>
  );
};

export default ResetPasswordPage;